import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseToolOutput } from "./shared/utils";

interface LabelResult {
  ok: true;
  thread_id?: string;
  added?: string[];
  removed?: string[];
  message?: string;
}

interface LabelActionCardProps {
  part: ToolUIPart;
}

export const LabelActionCard = memo(function LabelActionCard({ part }: LabelActionCardProps) {
  const isStreaming = part.state === "input-streaming";

  const result = useMemo(
    () => parseToolOutput<LabelResult>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const labelResult = result.kind === "ok" ? result.data : null;

  const accent = isError ? "error" : "success";
  const status = isStreaming ? "streaming" : isError ? "error" : "ready";

  function buildDetails(): string {
    if (!labelResult) return "";
    const parts: string[] = [];
    if (labelResult.added?.length) parts.push(`Added: ${labelResult.added.join(", ")}`);
    if (labelResult.removed?.length) parts.push(`Removed: ${labelResult.removed.join(", ")}`);
    return parts.join(" · ");
  }

  return (
    <CardShell
      icon={isError ? "✗" : "✓"}
      title={isError ? "Label Error" : "Labels Updated"}
      accent={accent}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">⋯</span>
          <div className="tool-card__status-text">Updating labels...</div>
        </div>
      )}
      {isError && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">✗</span>
          <div>
            <div className="tool-card__status-text">Label operation failed</div>
            <div className="tool-card__status-detail">{result.error}</div>
          </div>
        </div>
      )}
      {!isStreaming && !isError && labelResult && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">✓</span>
          <div>
            <div className="tool-card__status-text">
              {labelResult.message ?? "Labels updated"}
            </div>
            {(() => {
              const details = buildDetails();
              return details ? <div className="tool-card__status-detail">{details}</div> : null;
            })()}
          </div>
        </div>
      )}
    </CardShell>
  );
});
